/**
 * 次の GC (ゲートクローズ) カウントダウン表示。
 * page ヘッダ右端に表示する。
 *
 * 当日断面のみ表示する (spec AC-DFC-CD-001 準拠。design L1835-1859 `renderGC` は
 * モード無条件に実行される = 全断面常時表示だが、翌日・翌々日断面は GC による
 * 編集ロックがなく全コマ編集可能 (demand-forecast-common.md:101) のため
 * カウントダウン表示自体を出さない方針で design より spec を優先する。
 * 制度上は GC = 受渡コマ開始の 1 時間前なので翌日 00:00-00:30 コマの GC は
 * 当日 23:00 に到来する (backend `gate_close.go:20-23` の cross-midnight 参照)
 * が、それは backend `gc_koma` (= 当日 workflow のロック対象) の話であり、
 * 翌日 workflow 側 (= section=TOMORROW) の Toolbar/画面には出さない、が spec 準拠。
 *
 * 断面ゲート (section=TODAY か) は呼び出し元 index.tsx の headerExtra 側で行うが、
 * 「対象日 (workflow の target_date) が壁時計の今日と一致するか」の判定は本
 * コンポーネント内で行う (`targetDate` prop + wall clock now を isSameDay で比較)。
 * 目的: 過去日 (例: 8/5 の当日断面ケース) を後日 (8/6) に振り返り閲覧した場合や、
 * 23:5x に開いたまま日跨ぎで壁時計だけ翌日になった場合に、対象日と無関係の GC 残り
 * 時間を表示し続けるのを防ぐ (issue #4265 の残存経路潰し)。
 *
 * 次にロックされるコマと締切は、page 応答の backend の GC 境界 (`gate_close_frontier`)
 * を `~/models/gateClose` で現在時刻まで進めて求める (壁時計から GC コマを再計算しない。
 * GC の余裕時間・日跨ぎは backend の `GateCloseFrontier` だけが持つ)。page の取得前は
 * 境界が無いので render しない。
 * 1 秒間隔で残り時間を更新する (G4, spec AC-DFC-CD-001「時:分:秒 相当」表示 +
 * 1 秒ごとの更新。design L1781-1805 は 1 分間隔だったが spec 準拠のため秒間隔に
 * 変更 — design 側の分間隔更新は視覚デザインの参照時点のものであり、本コンポーネ
 * ントの更新頻度は spec が SoT)。
 *
 * 見た目は全画面共通の [[GcCountdownPill]] (design `.gc-countdown`、 2026-10-05 統合)。
 * 「GC まで」 残り時間 | 「次の GC」 コマ・時間帯 の順。 残時間の桁は
 * `formatGcRemainingTime` で時間前入札 (`~/pages/IntradayBidding/components/GcCountdown`) と
 * 揃える (issue #4858)。 本 component は何を数えるかと計算だけを持ち、 統合しない —
 * GC の選び方が両画面で異なる (本画面は当日受渡の全 48 コマを数え、 コマ 48 の GC
 * (22:30 JST) 以降は「本日分の GC は終了」 を出す (#9790) / 時間前は境界ごとに次の
 * GC を取り直す) ためで、 これは表記ではなく業務挙動の差。
 */
import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { GcCountdownPill } from '~/components/common/GcCountdownPill'
import { now as serverNow } from '~/models/clock'
import { isTargetDateTodayInJst } from '~/models/dateTime'
import type { GateCloseFrontier } from '~/models/gateClose'
import { gateCloseCountdownOn } from '~/models/gateClose'
import { formatGcRemainingTime } from '~/models/gcRemainingTime'
import { komaNumberTimeRange } from '~/models/komaToTime'

/** G4 (spec AC-DFC-CD-001): 1 秒ごとに残り時間を更新する */
const UPDATE_INTERVAL_MS = 1_000

/** design JS: `if (diffMin < 10) remainEl.classList.add('gc-urgent')` (= 600秒) */
const URGENT_THRESHOLD_SECONDS = 10 * 60

/**
 * GC カウントダウン。
 *
 * @param targetDate 対象日 (workflow の target_date、`google.type.Date` = JST カレンダー日)。
 *   壁時計の今日 (JST 基準) と一致しない場合 (過去日を後日振り返り閲覧 /
 *   23:5x 開いたまま日跨ぎ) は render しない。`undefined` は「まだ workflow を
 *   fetch 中 = 対象日不明」を表し、この場合も render しない (対象日確定後に判定する)。
 * @param gateCloseFrontier page 応答の GC 境界。`undefined` (page 取得前) は render しない。
 */
export const GcCountdown = ({
  targetDate,
  gateCloseFrontier,
}: {
  targetDate: { year: number; month: number; day: number } | undefined
  gateCloseFrontier: GateCloseFrontier | undefined
}): React.ReactElement | null => {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => serverNow())

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(serverNow())
    }, UPDATE_INTERVAL_MS)
    return () => {
      clearInterval(timer)
    }
  }, [])

  // targetDate が未取得 / 壁時計 (JST 基準) の今日と乖離している場合は render しない
  // (issue #4265 の残存経路潰し: 過去日ケースを後日開く / 日跨ぎ)。
  // isTargetDateTodayInJst は proto Date をローカルタイムゾーンで解釈しない
  // (backend valueobject.IsTargetDateToday と対応) ため、PDT 等 non-JST ブラウザで
  // JST 8/7 の対象日が local 8/6 として扱われて GC が常に非表示になる問題
  // (PR #4945 レビュー指摘、fromProtoDate + isSameDay を使っていた旧実装のバグ) を回避する。
  if (
    targetDate === undefined ||
    gateCloseFrontier === undefined ||
    !isTargetDateTodayInJst(targetDate, now)
  ) {
    return null
  }

  const countdown = gateCloseCountdownOn(gateCloseFrontier, targetDate, now)
  switch (countdown.status) {
    case 'notStarted':
      return null
    case 'allClosed':
      // 当日受渡の全 48 コマが GC 済み (22:30 JST 以降)。翌日 0:00 開始のコマは数えず
      // 終了表示にする (#9790、 design ui-feasibility/components/countdown.md「需要想定
      // 当日の終了判定」)。終了表示は状態の文言だけで、GcCountdownPill は passedText が
      // ある時 remaining・slot を描かない。
      return (
        <GcCountdownPill
          passedText={t('page:demandForecastDaily.header.gcPassed')}
          remaining=""
        />
      )
    case 'counting':
      return (
        <GcCountdownPill
          remaining={formatGcRemainingTime(countdown.remainingSeconds)}
          // 時間帯の区切りは spec conventions「時刻表示」の 〜 (ja)、 en は `-` (design countdown.md)。
          slot={{
            koma: countdown.koma,
            range: komaNumberTimeRange(
              countdown.koma,
              t('common:gcCountdown.rangeSeparator'),
            ),
          }}
          urgent={countdown.remainingSeconds < URGENT_THRESHOLD_SECONDS}
        />
      )
  }
}
